import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { useActiveOrg } from '@/hooks/use-active-org'
import { useLocale } from '@/hooks/use-locale'
import { resolveBreadcrumbConfig } from '@/utils/breadcrumb-config'
import { isOrgRole } from '@/utils/role-check.utils'
import React from 'react'
import { Link, useLocation } from 'react-router'

const renderBreadcrumbItem = (item: { label: string; href?: string; isCurrentPage?: boolean }) => {
  if (item.isCurrentPage) return <BreadcrumbPage>{item.label}</BreadcrumbPage>
  if (item.href) {
    return (
      <BreadcrumbLink asChild>
        <Link to={item.href}>{item.label}</Link>
      </BreadcrumbLink>
    )
  }
  return <BreadcrumbLink href='#'>{item.label}</BreadcrumbLink>
}

export const AutoBreadcrumb: React.FC = () => {
  const { t } = useLocale()
  const location = useLocation()
  const { activeOrg } = useActiveOrg()
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/'
  const currentBreadcrumbs = resolveBreadcrumbConfig(normalizedPath, t, location.search)

  if (currentBreadcrumbs.length === 0) return null

  // Function to render breadcrumb items with responsive truncation
  const renderBreadcrumbItems = () => {
    const allItems = [{ label: 'Dashboard', href: '/', isCurrentPage: false }, ...currentBreadcrumbs]

    // For small screens (less than sm breakpoint), show only first and last item with ellipsis
    const shouldTruncate = allItems.length > 2
    const homeLabel = activeOrg?.role && isOrgRole(activeOrg?.role) ? `GRC Operations ${t('centre')}` : 'Dashboard'

    return (
      <>
        {/* Always show first item (Dashboard) */}
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to='/'>{homeLabel}</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>

        {normalizedPath === '/' ? null : <BreadcrumbSeparator />}

        {shouldTruncate ? (
          <>
            {/* Show ellipsis on small screens, hide on larger screens */}
            <BreadcrumbItem className='block sm:hidden'>
              <BreadcrumbEllipsis />
            </BreadcrumbItem>
            <BreadcrumbSeparator className='block sm:hidden' />

            {/* Show middle items only on larger screens */}
            <div className='hidden sm:contents'>
              {allItems.slice(1, -1).map((item, index) => (
                <React.Fragment key={`middle-${index}`}>
                  <BreadcrumbItem>{renderBreadcrumbItem(item)}</BreadcrumbItem>
                  <BreadcrumbSeparator />
                </React.Fragment>
              ))}
            </div>

            {/* Always show last item when truncating */}
            {allItems.length > 1 && (
              <BreadcrumbItem>{renderBreadcrumbItem(allItems[allItems.length - 1])}</BreadcrumbItem>
            )}
          </>
        ) : (
          allItems
            .slice(1)
            .map((item, index) => <BreadcrumbItem key={`trail-${index}`}>{renderBreadcrumbItem(item)}</BreadcrumbItem>)
        )}
      </>
    )
  }

  return (
    <Breadcrumb className='min-w-0 flex'>
      <BreadcrumbList>{renderBreadcrumbItems()}</BreadcrumbList>
    </Breadcrumb>
  )
}
